import { cn } from "@/lib/utils";

type Founder = {
  initials: string;
  name: string;
  role: string;
  bio: string;
};

const FOUNDERS: Founder[] = [
  {
    initials: "KJ",
    name: "Kyle Jekielek",
    role: "Co-Founder & CEO",
    bio: "A New York native now based in Detroit, Kyle has spent 25 years in the music industry as a producer, audio engineer, DJ, and performing artist, and founded the record label Crown Digital Records. He knows firsthand how much unnecessary work goes into getting music ready for release. That's why he started Lutely.",
  },
  {
    initials: "JR",
    name: 'Jonathan "JR" Rochelle',
    role: "Co-Founder & Chief Product Officer",
    bio: "Jonathan, known as JR, was formerly a Director of Product Management at Google, where he's best known for co-creating Google Sheets alongside Farzad \"Fuzzy\" Khosrowshahi. At Lutely, he's focused on the same thing: making a release faster to put out, not fancier.",
  },
];

const EARLY_ADOPTERS = [
  "NEMS",
  "Deante' Hitchcock",
  "Roc Marciano",
  "Benny the Butcher",
  "Ransom",
  "Dave East",
  "V Don",
  "ElCamino",
];

function SectionHeading({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h2
      className={cn(
        "flex items-center gap-[10px] font-figtree text-[24px] font-bold tracking-[-0.24px] text-blue-900",
        className,
      )}
    >
      <span
        aria-hidden
        className="h-[10px] w-[10px] shrink-0 rounded-[2px] bg-teal-500"
      />
      {children}
    </h2>
  );
}

export default function AboutContent() {
  return (
    <main className="mx-auto w-full max-w-[760px] px-6 font-open-sans text-blue-900">
      {/* Hero */}
      <section className="pb-10 pt-16">
        <SectionHeading className="mb-5">About Lutely</SectionHeading>

        <p className="mb-4 text-[16px] leading-[1.6] text-gray-600">
          Lutely is where artists sell their music direct, keep their ownership,
          and get paid 100% of every sale. No middlemen. No approval queue. Just
          music, sold directly to the people who want it.
        </p>
      </section>

      {/* Why we started Lutely */}
      <section className="border-t border-gray-300 py-11">
        <SectionHeading className="mb-5">Why we started Lutely</SectionHeading>
        <p className="mb-4 text-[16px] leading-[1.6] text-gray-600">
          Getting a release ready for streaming platforms is unpaid, unglamorous
          work, and the payout rarely covers it. Lutely automates that work, so
          artists and labels spend less time on logistics and keep more of what
          a fan actually pays.
        </p>
        <p className="text-[16px] leading-[1.6] text-gray-600">
          Kyle Jekielek and Jonathan &ldquo;JR&rdquo; Rochelle started Lutely to
          fix that.
        </p>
      </section>

      {/* Founders */}
      <section className="border-t border-gray-300 py-11">
        <SectionHeading className="mb-5">Founders</SectionHeading>
        <div className="flex flex-col gap-9">
          {FOUNDERS.map((founder) => (
            <div
              key={founder.name}
              className="grid grid-cols-[64px_1fr] gap-6 md:grid-cols-[120px_1fr]"
            >
              <div
                aria-hidden
                className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 font-figtree text-[18px] font-bold text-blue-900 md:h-20 md:w-20 md:text-[22px]"
              >
                {founder.initials}
              </div>
              <div>
                <h3 className="mb-0.5 font-figtree text-[18px] font-bold text-blue-900">
                  {founder.name}
                </h3>
                <p className="mb-[10px] text-[14px] font-semibold text-brand-primary">
                  {founder.role}
                </p>
                <p className="text-[16px] leading-[1.6] text-gray-600">
                  {founder.bio}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* The team */}
      <section className="border-t border-gray-300 py-11">
        <SectionHeading className="mb-5">The team</SectionHeading>
        <p className="text-[16px] leading-[1.6] text-gray-600">
          Alongside Kyle and JR, Jon Jekielek serves as Executive Vice President
          and Nick Schaubeck serves as Chief Technology Officer.
        </p>
      </section>

      {/* Early adopters */}
      <section className="border-t border-gray-300 py-11">
        <SectionHeading className="mb-5">Early adopters</SectionHeading>
        <ul className="flex list-none flex-wrap gap-[10px] p-0">
          {EARLY_ADOPTERS.map((name) => (
            <li
              key={name}
              className="rounded-[20px] border border-gray-300 bg-gray-100 px-3 py-1.5 text-[14px] text-blue-900"
            >
              {name}
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
